import type { PollVote } from "../../hooks/usePollVotes";
import type { PollInsights, Slot, SlotScore, VoteType } from "./types";

const VALID_VOTES: ReadonlySet<VoteType> = new Set(["yes", "maybe", "no"]);

/**
 * Calcule les scores par slot et le gagnant à partir de la liste canonique
 * de slots et des votes bruts (poll_responses).
 *
 * Règles :
 * - Chaque row de poll_responses = 1 participant (dédupé par respondent_name)
 * - Un vote absent sur un slot → `empty++`
 * - `winnerSlotId` = argmax sur `yes`. En cas d'égalité, premier dans l'ordre
 *   chronologique (= ordre des slots, puisque les slots sont ordonnés par index).
 * - Les slots sans aucun vote sont scorés à zéro (pas omis).
 */
export function computeSlotScores(
  slots: Slot[],
  votes: PollVote[],
): PollInsights {
  // Dédup participants par respondent_name (un row par participant côté DB via
  // contrainte unique poll_responses_poll_respondent_type_uidx, mais on reste
  // défensif au cas où).
  const seen = new Set<string>();
  const uniqueVotes: PollVote[] = [];
  for (const vote of votes) {
    const key = vote.respondent_name ?? vote.id;
    if (seen.has(key)) continue;
    seen.add(key);
    uniqueVotes.push(vote);
  }

  const participantsCount = uniqueVotes.length;

  const scores: SlotScore[] = slots.map((slot) => {
    const score: SlotScore = {
      slotId: slot.id,
      yes: 0,
      maybe: 0,
      no: 0,
      empty: 0,
      total: participantsCount,
    };

    for (const vote of uniqueVotes) {
      const value = vote.response_data?.[slot.id];
      if (value && VALID_VOTES.has(value)) {
        score[value]++;
      } else {
        score.empty++;
      }
    }

    return score;
  });

  let winnerSlotId: string | null = null;
  let maxYes = 0;
  for (const score of scores) {
    if (score.yes > maxYes) {
      maxYes = score.yes;
      winnerSlotId = score.slotId;
    }
  }
  // Si aucun vote yes, pas de gagnant défini (reste null).
  // Si des slots existent mais personne n'a voté yes, on laisse winnerSlotId null
  // pour que l'UI affiche un état neutre.

  return {
    scores,
    winnerSlotId,
    maxYes,
    participantsCount,
  };
}
